import { type RootState } from '@/store';
import { useSelector } from 'react-redux';

/**
 * Reactive read of the raw auth token (`state.auth.token`). Rematch
 * containment: new `@/store` imports are frozen — consumers take this hook
 * instead. Injected into `@heidi/workspace/data` as its `useAuthToken` config
 * seam (`src/composition/hosts/workspace/workspace-data-wiring.ts`); retires with the SSOT rollout.
 *
 * Lives in its own module, and re-exports from `useIsAuthenticated.ts` rather
 * than the other way round, because the module graph is load-bearing: the
 * query hooks that only need the token guard are leaves, and
 * `useIsAuthenticated` reaches the Rematch store eagerly through
 * `getEffectiveAuthProvider`. Importing the token from there puts every model
 * in front of a leaf hook and closes a cycle back into it — `useQueryChats` →
 * store → models → `useQueryUser` → `useQueryChats`, which throws on
 * `GET_CHATS` before initialization. The `@/store` import below is type-only
 * and erases, so this module reaches nothing at run time but `react-redux`.
 */
export const useAuthToken = (): string | null | undefined =>
  useSelector((state: RootState) => state.auth.token);
